import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/Badge';
import { apiFetch } from '../../api/client';
import { computeDiff } from '@infinia/ida-sdk/pex';
import type {
  PresentationDefinition,
  VerifiableCredential,
  VerifiablePresentation,
  EvaluationOutcome,
  PresentationSubmission,
} from '@infinia/ida-sdk';
import type { DiffResult } from '@infinia/ida-sdk/pex';
import { encodePathSegment } from '../../lib/url';

/**
 * Phase 7-C — Holder web mirror page (`/credentials/respond/:requestId`).
 *
 * Lets a holder respond to a verifier's PEx v2 Presentation Definition from
 * the desktop browser when the mobile wallet isn't available. Reuses the
 * shared SDK `computeDiff` module — exact same logic as the wallet —
 * architecture §4.9, §4.10.
 *
 * Flow:
 *  1. Fetch the public PD body via `apiFetch('/presentations/definitions/:id')`
 *     (no-auth public endpoint per `02_api_spec.md` §7).
 *  2. List the holder's local credentials via the existing `/credentials` endpoint.
 *  3. Render the diff (`asked-for` vs `you'll reveal`) using `computeDiff`.
 *  4. For BBS+ credentials, the user can toggle individual revealed fields.
 *  5. On Submit, POST a server-built VP into `/presentations/evaluate` and
 *     surface the per-input-descriptor outcomes inline.
 */

interface PublicPDBody {
  definition: PresentationDefinition;
  challenge: string;
  domain: string;
  expiresAt: string;
}

interface CredentialRow {
  id: string;
  schema?: string;
  type?: string[];
  credentialSubject?: Record<string, unknown>;
  proof?: { type: string };
  // Whatever else the API returns is preserved verbatim.
  [k: string]: unknown;
}

export default function RespondToRequest() {
  const { requestId = '' } = useParams<{ requestId: string }>();
  const { t } = useTranslation(['respond', 'verifier']);

  const [pd, setPd] = useState<PublicPDBody | null>(null);
  const [pdError, setPdError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<VerifiableCredential[]>([]);
  const [loading, setLoading] = useState(true);

  // Per-descriptor "user toggled this field on/off" state for BBS+ flows.
  // Keyed `${descriptorId}::${fieldName}` → boolean (default true).
  const [fieldSelections, setFieldSelections] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<EvaluationOutcome | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch the PD (public, no-auth) and the holder's credentials in parallel.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      apiFetch<PublicPDBody>(`/presentations/definitions/${encodePathSegment(requestId)}`),
      apiFetch<{ items?: CredentialRow[] } | CredentialRow[]>('/credentials?subject=current'),
    ])
      .then(([pdRes, credRes]) => {
        if (cancelled) return;
        if (pdRes.status === 'fulfilled') {
          setPd(pdRes.value);
          setPdError(null);
        } else {
          const err = pdRes.reason as { message?: string; code?: string };
          // Surface PD_NOT_FOUND / PD_REVOKED distinctly per orchestrator decision.
          setPdError(err.code ?? err.message ?? 'PD_FETCH_FAILED');
        }
        if (credRes.status === 'fulfilled') {
          const arr = Array.isArray(credRes.value)
            ? credRes.value
            : ((credRes.value as { items?: CredentialRow[] }).items ?? []);
          setCredentials(arr as unknown as VerifiableCredential[]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const diff: DiffResult | null = useMemo(() => {
    if (!pd) return null;
    const draftVp: VerifiablePresentation = {
      '@context': ['https://www.w3.org/2018/credentials/v1'],
      id: 'urn:uuid:draft',
      type: ['VerifiablePresentation'],
      holder: 'did:ida:holder:current',
      verifiableCredential: credentials,
    };
    return computeDiff(pd.definition, draftVp);
  }, [pd, credentials]);

  const toggleField = (descriptorId: string, fieldName: string) => {
    const k = `${descriptorId}::${fieldName}`;
    setFieldSelections((s) => ({ ...s, [k]: s[k] === false ? true : false }));
  };
  const isFieldOn = (descriptorId: string, fieldName: string) => {
    const k = `${descriptorId}::${fieldName}`;
    return fieldSelections[k] !== false;
  };

  const handleSubmit = async () => {
    if (!pd || !diff) return;
    setSubmitting(true);
    setSubmitError(null);
    setOutcome(null);
    try {
      // Build a presentation_submission descriptor_map by walking the diff —
      // each descriptor maps to the candidate VC at its current position.
      const descriptor_map = diff.perDescriptor
        .filter((d) => d.candidateCredentialId)
        .map((d, idx) => ({
          id: d.descriptorId,
          format: 'ldp_vc' as const,
          path: `$.verifiableCredential[${idx}]`,
        }));
      const submission: PresentationSubmission = {
        id: `sub_${Date.now()}`,
        definition_id: pd.definition.id,
        descriptor_map,
      };

      // Server-side VP signing — the existing `/presentations/create` endpoint
      // wraps the holder's selected credentials with the wallet-managed key.
      const credentialIds = diff.perDescriptor
        .map((d) => d.candidateCredentialId)
        .filter((id): id is string => !!id);
      const vp = await apiFetch<VerifiablePresentation>('/presentations/create', {
        method: 'POST',
        body: JSON.stringify({
          credentialIds,
          challenge: pd.challenge,
          domain: pd.domain,
        }),
      });

      const result = await apiFetch<EvaluationOutcome>('/presentations/evaluate', {
        method: 'POST',
        body: JSON.stringify({
          presentationDefinitionId: pd.definition.id,
          presentation: vp,
          presentationSubmission: submission,
          challenge: pd.challenge,
          domain: pd.domain,
        }),
      });
      setOutcome(result);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      setSubmitError(err.code ?? err.message ?? 'SUBMIT_FAILED');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (pdError) {
    const isNotFound = pdError === 'PD_NOT_FOUND';
    const isRevoked = pdError === 'PD_REVOKED';
    return (
      <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-3 p-6 text-center">
        <span className="material-symbols-outlined text-red-500" style={{ fontSize: 48 }}>
          {isNotFound ? 'search_off' : isRevoked ? 'block' : 'error'}
        </span>
        <h1 className="text-xl font-bold text-text-main">
          {isNotFound ? t('respond:errors.notFound') : isRevoked ? t('respond:errors.revoked') : t('respond:errors.fetchFailed')}
        </h1>
        <p className="text-sm text-text-muted">
          {isNotFound
            ? t('respond:errors.notFoundHint')
            : isRevoked
            ? t('respond:errors.revokedHint')
            : pdError}
        </p>
      </div>
    );
  }

  if (!pd || !diff) return null;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-text-main">{t('respond:title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('respond:subtitle')}</p>
        {pd.definition.purpose && (
          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            <span className="font-bold">{t('respond:verifierPurpose')}: </span>
            {pd.definition.purpose}
          </div>
        )}
      </div>

      {outcome ? (
        <OutcomeView outcome={outcome} t={t} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {diff.perDescriptor.map((d) => (
              <div key={d.descriptorId} className="grid grid-cols-2 gap-0 rounded-xl border border-border-subtle bg-white shadow-sm">
                {/* Left: asked for */}
                <div className="border-r border-border-subtle p-4">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('respond:asked')}</div>
                  <div className="mt-1 text-sm font-bold text-text-main">{d.descriptorName ?? d.descriptorId}</div>
                  {d.descriptorPurpose && <p className="mt-1 text-xs text-text-muted">{d.descriptorPurpose}</p>}
                  <div className="mt-3 space-y-1">
                    {d.askedFor.map((p) => (
                      <div key={p} className="font-mono text-[11px] text-text-main">{p}</div>
                    ))}
                  </div>
                  {d.willProvePredicate.length > 0 && (
                    <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                      {t('respond:provePredicate', { count: d.willProvePredicate.length })}
                    </div>
                  )}
                </div>

                {/* Right: you'll reveal */}
                <div className="p-4">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('respond:reveal')}</div>
                  {d.candidateCredentialId ? (
                    <>
                      <div className="mt-1 truncate font-mono text-[11px] text-text-main">{d.candidateCredentialId}</div>
                      <div className="mt-3 space-y-1.5">
                        {d.willReveal.map((f) => (
                          <label key={f} className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              checked={isFieldOn(d.descriptorId, f)}
                              onChange={() => toggleField(d.descriptorId, f)}
                            />
                            <span className="font-mono text-text-main">{f}</span>
                          </label>
                        ))}
                      </div>
                      {d.willHide.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-text-faint">{t('respond:hidden')}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {d.willHide.map((f) => (
                              <span key={f} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                      {t('respond:noMatch')}
                    </div>
                  )}
                  {d.warnings.length > 0 && (
                    <div className="mt-2 space-y-0.5 text-[11px] text-amber-700">
                      {d.warnings.map((w, i) => (
                        <div key={i}>⚠ {w}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-white p-4 shadow-sm">
            <div className="flex-1 text-xs text-text-muted">
              {t('respond:totalsSummary', {
                asked: diff.totalAsked,
                revealed: diff.totalRevealed,
                proven: diff.totalProvenWithoutRevealing,
              })}
            </div>
            <button onClick={handleSubmit} disabled={submitting} className="btn-primary">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
              {submitting ? t('respond:submitting') : t('respond:sign')}
            </button>
          </div>

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {submitError === 'PD_NOT_FOUND' || submitError === 'PD_REVOKED'
                ? t(`respond:errors.${submitError === 'PD_NOT_FOUND' ? 'notFound' : 'revoked'}`)
                : submitError}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OutcomeView({
  outcome,
  t,
}: {
  outcome: EvaluationOutcome;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="space-y-4">
      <div
        className={`rounded-xl border-2 p-6 text-center ${
          outcome.overallSatisfied ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
        }`}
      >
        <span
          className={`material-symbols-outlined ${
            outcome.overallSatisfied ? 'text-emerald-500' : 'text-red-500'
          }`}
          style={{ fontSize: 48 }}
        >
          {outcome.overallSatisfied ? 'verified' : 'gpp_bad'}
        </span>
        <h2 className={`mt-2 text-xl font-bold ${outcome.overallSatisfied ? 'text-emerald-700' : 'text-red-700'}`}>
          {outcome.overallSatisfied ? t('respond:outcome.satisfied') : t('respond:outcome.notSatisfied')}
        </h2>
      </div>
      <div className="space-y-2">
        {outcome.descriptorOutcomes.map((d) => (
          <div key={d.descriptorId} className="flex items-start gap-3 rounded-lg border border-border-subtle bg-white p-3 shadow-sm">
            <Badge variant={d.satisfied ? 'success' : 'error'}>{d.satisfied ? '✓' : '✗'}</Badge>
            <div className="flex-1">
              <div className="font-mono text-xs font-bold text-text-main">{d.descriptorId}</div>
              {d.failureReason && (
                <div className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600">
                  {t(`verifier:pex.failureReasons.${d.failureReason}`, { defaultValue: d.failureReason })}
                </div>
              )}
              {d.revealedFields && d.revealedFields.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {d.revealedFields.map((f) => (
                    <span key={f} className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] text-blue-700">{f}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
